import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IApiMTradingAccount } from '@api/interfaces/data-contracts';
import { apiTradingAccountUser } from '@api/services/tradingAccountUser.apiService';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { useBrokerParams } from '@utils/hooks/useBrokerParams';
import { Flex, Switch } from 'antd';
import styles from './styles.module.scss';

interface IAccountDetailsNotificationsProps {
  account: IApiMTradingAccount;
  onConfirm: () => Promise<void>;
}

type AccountNotificationSettingsKey = keyof Pick<IApiMTradingAccount, 'isNotificationsEnabled'>;

interface AccountNotificationSettings {
  key: AccountNotificationSettingsKey;
  label: string;
  description: string;
}

const ACCOUNT_NOTIFICATION_SETTINGS: AccountNotificationSettings[] = [
  {
    key: 'isNotificationsEnabled',
    label: keyMap.tradingAccountPushNotificationLabel,
    description: keyMap.tradingAccountsPushNotificationHint,
  },
];

export const AccountDetailsNotifications = ({ account, onConfirm }: IAccountDetailsNotificationsProps) => {
  const { i18n } = useTranslation();

  const { control, setValue } = useForm<Record<AccountNotificationSettingsKey, boolean>>({
    defaultValues: {
      isNotificationsEnabled: account.isNotificationsEnabled,
    },
  });

  const brokerParams = useBrokerParams();

  const brokerName = brokerParams.currentLanguage?.brokerName;

  const [loadingStates, setLoadingStates] = useState<Record<AccountNotificationSettingsKey, boolean>>({
    isNotificationsEnabled: false,
  });

  const updateSetting = async (key: AccountNotificationSettingsKey, value: boolean) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [key]: true }));

      await apiTradingAccountUser.setAccountNotification({ tradingAccountId: account.id, enabled: value });
      await onConfirm();
    } catch (error) {
      handleError(keyMap.sharedErrorsFailedToSaveSettings);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleToggle = (key: AccountNotificationSettingsKey, value: boolean) => {
    setValue(key, value);

    void updateSetting(key, value);
  };

  return (
    <div className="form-box__row">
      <div className="form-box form-box--columns">
        {ACCOUNT_NOTIFICATION_SETTINGS.map((setting) => {
          return (
            <Flex key={setting.key} justify="space-between">
              <div>
                <h4 className={styles.title}>{i18n.t(setting.label, { brokerName: brokerName ?? '' })}</h4>
                <span className={styles.hint}>{i18n.t(setting.description, { brokerName: brokerName ?? '' })}</span>
              </div>

              <Controller
                name={setting.key}
                control={control}
                render={({ field }) => (
                  <Switch
                    loading={loadingStates[setting.key]}
                    onChange={(e) => handleToggle(setting.key, e)}
                    checked={field.value}
                  />
                )}
              />
            </Flex>
          );
        })}
      </div>
    </div>
  );
};
